/**
 * PackingProductsScreen - Pantalla principal de empacado de productos
 *
 * Muestra la lista de productos de una orden específica que está siendo empacada.
 * Incluye:
 * - Información del pedido y tienda
 * - Estadísticas de productos totales vs empacados
 * - Lista de productos con sus cantidades esperadas y procesadas
 * - Botones para abrir modales de productos y pedidos del packing
 * - Integración con el flujo de packing para asignar cantidades
 */

import { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag, faShoppingCart } from '@fortawesome/pro-light-svg-icons';
import { Avatar, AvatarImage, AvatarFallback } from '@shared/components/ui/shadcn/avatar';
import { Button } from '@shared/components/ui/button';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { ProductRow } from '@modules/OrderPreparation/shared';
import { LocationsModal, PackingProductsModal, PackingOrdersModal } from '../modals';
import { usePackingContext } from '../../hooks';
import { type ProductInfo, type Location } from '@modules/OrderPreparation/shared';

export interface PackingProductsScreenProps {
  selectedProduct?: any;
  selectedOrder?: any;
  assignedQuantities?: Record<string, number>;
  onBack?: () => void;
  isSendingOrder?: boolean;
}

export const PackingProductsScreen = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectedProduct: _selectedProductUnused,
  selectedOrder: selectedOrderProp,
  assignedQuantities = {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBack: _onBackUnused,
  isSendingOrder = false,
}: PackingProductsScreenProps) => {
  // Logs eliminados
  const { t } = useTranslation('packingModule');
  const { getProductLocations, products, orders, data } = usePackingContext();

  // Estado para los modales
  const locationsModalRef = useRef<any>(null);
  const packingProductsModalRef = useRef<any>(null);
  const packingOrdersModalRef = useRef<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfo | undefined>(undefined);
  const [productLocations, setProductLocations] = useState<Location[]>([]);

  // Handler para abrir el modal de ubicaciones
  const handleLocationModalOpen = async (product: any) => {
    try {
      // Obtener ubicaciones reales desde la API
      const productId = parseInt(product.id);
      const locations = await getProductLocations(productId);

      // Convertir a ProductInfo para el modal
      const productInfo: ProductInfo = {
        id: product.id,
        name: product.name,
        code: product.code,
        image: product.image,
      };

      setSelectedProduct(productInfo);
      setProductLocations(locations);
      locationsModalRef.current?.openModal();
    } catch {
      // Error al obtener ubicaciones - mostrar ubicaciones vacías
      const productInfo: ProductInfo = {
        id: product.id,
        name: product.name,
        code: product.code,
        image: product.image,
      };
      setSelectedProduct(productInfo);
      setProductLocations([]);
      locationsModalRef.current?.openModal();
    }
  };

  // Handler para abrir el modal de productos del packing
  const handlePackingProductsModalOpen = () => {
    packingProductsModalRef.current?.openModal();
  };

  // Handler para abrir el modal de pedidos del packing
  const handlePackingOrdersModalOpen = () => {
    packingOrdersModalRef.current?.openModal();
  };

  // Función para obtener assignedQuantities del localStorage (misma lógica que los modales)
  const getAssignedQuantities = () => {
    try {
      const routeKey = `packing:assigned:${data?.preparation?.id || 'unknown'}`;
      const assigned: Record<string, number> = JSON.parse(localStorage.getItem(routeKey) || '{}');
      return assigned;
    } catch {
      return {};
    }
  };

  // Verificar si hay productos en packing para mostrar el botón
  const hasProductsInPacking = useMemo(() => {
    const assigned = getAssignedQuantities();
    const hasFromAssigned = Object.values(assigned).some((qty) => Number(qty) > 0);
    const hasFromApi = (products || []).some((p: any) => {
      const code = p?.code;
      const assignedForProduct = Object.entries(assigned)
        .filter(([key]) => key.endsWith(`:${code}`))
        .reduce((sum, [, qty]) => sum + Number(qty), 0);
      const processed = Math.min((p?.processedQuantity || 0) + assignedForProduct, p?.expectedQuantity || 0);
      return processed > 0;
    });
    return hasFromAssigned || hasFromApi;
  }, [products, data?.preparation?.id, assignedQuantities]);

  // Verificar si hay pedidos con productos incompletos para mostrar el botón
  const hasOrdersWithIncompleteProducts = useMemo(() => {
    if (!orders || orders.length === 0) return false;

    return orders.some((order: any) => {
      if (!order.lines) return false;

      return order.lines.some((line: any) => {
        if (line.pack && line.pack.lines) {
          // Es un pack - verificar cada producto del pack
          return line.pack.lines.some((packLine: any) => {
            const productCode = packLine.product?.code;
            const expectedQty = packLine.qty || 1;
            const assignedQty = assignedQuantities[`${order.id}:${productCode}`] || 0;
            const processedQty = Math.min(assignedQty, expectedQty);
            return processedQty < expectedQty;
          });
        } else if (line.product) {
          // Es un producto individual
          const productCode = line.product.code;
          const expectedQty = line.qty || 1;
          const assignedQty = assignedQuantities[`${order.id}:${productCode}`] || 0;
          const processedQty = Math.min(assignedQty, expectedQty);
          return processedQty < expectedQty;
        }
        return false;
      });
    });
  }, [orders, data?.preparation?.id, assignedQuantities]);

  // Calcular estadísticas basadas en la orden seleccionada
  const stats = useMemo(() => {
    const orderLines = (selectedOrderProp?.lines || []) as any[];
    let totalProducts = 0;
    let packedProducts = 0;

    orderLines.forEach((line) => {
      if (line.pack && line.pack.lines) {
        // Para packs, contar cada producto individual del pack
        totalProducts += line.pack.lines.length;
        line.pack.lines.forEach((packLine: any) => {
          const packProductCode = packLine.product?.code || '';
          const packKey = `${selectedOrderProp?.id ?? ''}:${packProductCode}`;
          if ((assignedQuantities[packKey] || 0) > 0) {
            packedProducts++;
          }
        });
      } else if (line.product) {
        // Para productos individuales
        totalProducts++;
        const code = line.product?.code || '';
        const key = `${selectedOrderProp?.id ?? ''}:${code}`;
        if ((assignedQuantities[key] || 0) > 0) {
          packedProducts++;
        }
      }
    });

    return {
      total: totalProducts,
      packed: packedProducts,
    };
  }, [selectedOrderProp, assignedQuantities]);

  // (sin uso) productos completos de preparación

  return (
    <div>
      <SectionBox className="relative">
        {/* Spinner de guardado cuando se está enviando la orden */}
        {(() => {
          // Logs eliminados
          return isSendingOrder ? (
            <div className="absolute inset-0 bg-body-bg bg-opacity-90 flex items-center justify-center z-50 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-complementary-text">{t('packing_screen.saving_order')}</p>
              </div>
            </div>
          ) : null;
        })()}

        {/* Header con información del pedido/tienda */}
        <div className="flex items-center gap-3 mb-4">
          <Avatar className="w-8 h-8 border border-card-border">
            <AvatarImage src={selectedOrderProp?.shop?.image_hash} alt={selectedOrderProp?.shop?.name} />
            <AvatarFallback className="bg-card-border text-xs font-medium">
              {(selectedOrderProp?.shop?.name || '').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-sm font-medium text-title">{selectedOrderProp?.ref || '—'}</div>
            <div className="text-xs text-subtext">{selectedOrderProp?.shop?.name || '—'}</div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-complementary-text">
              {t('packing_screen.total_products')} <span className="text-text font-bold">({stats.total})</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-complementary-text">
              {t('packing_screen.packed_products')} <span className="text-text font-bold">({stats.packed})</span>
            </div>
          </div>
        </div>

        {/* Lista de productos requeridos por la orden detectada */}
        <div className="space-y-0">
          {(selectedOrderProp?.lines || [])
            .map((line: any, lineIndex: number) => {
              // Función auxiliar para construir URL completa de imagen
              const buildImageUrl = (imageHash: string) => {
                if (!imageHash) return '';
                if (imageHash.startsWith('http')) return imageHash;
                return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
              };

              const productsToRender: any[] = [];

              if (line.pack && line.pack.lines) {
                // Es un pack - mostrar cada producto individual del pack
                line.pack.lines.forEach((packLine: any, packIndex: number) => {
                  const productData = {
                    id: (packLine.product?.id || '').toString(),
                    name: packLine.product?.name || '',
                    code: packLine.product?.code || '',
                    image: buildImageUrl(packLine.product?.image_hash || ''),
                    expectedQuantity: line.qty, // La cantidad del pack se aplica a cada producto individual
                    processedQuantity: 0, // Se calculará abajo
                    isCompleted: false, // Se calculará abajo
                  };

                  // Calcular cantidad procesada para este producto individual del pack
                  const packProductCode = packLine.product?.code || '';
                  const packKey = `${selectedOrderProp?.id ?? ''}:${packProductCode}`;
                  const assigned = assignedQuantities[packKey] || 0;
                  productData.processedQuantity = Math.min(assigned, line.qty || 0);
                  productData.isCompleted = productData.processedQuantity >= (line.qty || 0);

                  productsToRender.push({
                    ...productData,
                    isPackProduct: true,
                    packIndex,
                    totalPackProducts: line.pack.lines.length,
                  });
                });
              } else if (line.product) {
                // Es un producto individual
                const productData = {
                  id: (line.product?.id || '').toString(),
                  name: line.product?.name || '',
                  code: line.product?.code || '',
                  image: buildImageUrl(line.product?.image_hash || ''),
                  expectedQuantity: line.qty,
                  processedQuantity: 0, // Se calculará abajo
                  isCompleted: false, // Se calculará abajo
                };

                // Calcular cantidad procesada para producto individual
                const code = productData.code;
                const key = `${selectedOrderProp?.id ?? ''}:${code}`;
                const assigned = assignedQuantities[key] || 0;
                productData.processedQuantity = Math.min(assigned, line.qty || 0);
                productData.isCompleted = productData.processedQuantity >= (line.qty || 0);

                productsToRender.push({
                  ...productData,
                  isPackProduct: false,
                });
              }

              // Renderizar todos los productos (packs desglosados + productos individuales)
              return productsToRender.map((productData, productIndex) => {
                const isLastLine = lineIndex === (selectedOrderProp?.lines?.length || 0) - 1;
                const isLastProductInLine = productIndex === productsToRender.length - 1;
                const showSeparator = !isLastLine || !isLastProductInLine;

                return (
                  <ProductRow
                    key={`${productData.id}-${lineIndex}-${productIndex}`}
                    product={productData}
                    onLocationClick={handleLocationModalOpen}
                    showSeparator={showSeparator}
                    dataTestIdPrefix="packing-screen-product"
                  />
                );
              });
            })
            .flat()}
        </div>

        {/* Mensaje cuando no hay productos */}
        {(!selectedOrderProp?.lines || selectedOrderProp?.lines.length === 0) && (
          <div className="text-center py-8">
            <p className="text-complementary-text text-xs">{t('packing_screen.no_products')}</p>
          </div>
        )}

        {/* Modal de ubicaciones */}
        <LocationsModal ref={locationsModalRef} product={selectedProduct} locations={productLocations} />
      </SectionBox>

      {/* Botones de acción - Solo visibles si hay contenido para mostrar */}
      {(hasProductsInPacking || hasOrdersWithIncompleteProducts) && (
        <div className="flex justify-end mt-2 gap-2 items-center">
          {hasProductsInPacking && (
            <Button
              variant="outline"
              size="default"
              onClick={handlePackingProductsModalOpen}
              data-testid="packing-products-modal-button"
            >
              <FontAwesomeIcon icon={faShoppingBag} className="w-3.5 h-3.5" />
            </Button>
          )}
          {hasOrdersWithIncompleteProducts && (
            <Button
              variant="outline"
              size="default"
              onClick={handlePackingOrdersModalOpen}
              data-testid="packing-orders-modal-button"
            >
              <FontAwesomeIcon icon={faShoppingCart} className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* Modal de productos del packing */}
      {/* @ts-expect-error ref no tipado en modal interno */}
      <PackingProductsModal ref={packingProductsModalRef as any} assignedQuantities={assignedQuantities} />

      {/* Modal de pedidos del packing */}
      {/* @ts-expect-error ref no tipado en modal interno */}
      <PackingOrdersModal ref={packingOrdersModalRef as any} />
    </div>
  );
};
